import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Container, Grid, Box, Typography, Button, Card,
  Chip, Divider, Table, TableBody, TableCell, TableContainer,
  TableRow, Paper, Skeleton, Alert, IconButton, Snackbar, Dialog, DialogContent,
  DialogTitle, DialogActions, TextField, CircularProgress, InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, ShoppingCart, Share, Close,
  LocalOffer, Store, VerifiedUser, CalendarToday, WatchLater, LocalShipping,
  Email as EmailIcon, OpenInNew as OpenInNewIcon, Handshake as HandshakeIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { fetchWatchById } from '../../services/ebayService';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_API_URL || 'https://watchpassion.vercel.app/api';

const fetchListingDetails = async (id) => fetchWatchById(id);

const ListingDetailsPage = () => {
  const { id: rawId } = useParams();
  const id = rawId.split('-')[0];
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [listing, setListing] = useState(location.state?.listing || null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [inquirySending, setInquirySending] = useState(false);
  const [inquirySent, setInquirySent] = useState(false);
  const [inquiryError, setInquiryError] = useState('');
  const [proposalOpen, setProposalOpen] = useState(false);
  const [proposalForm, setProposalForm] = useState({ name: '', email: '', phone: '', proposedPrice: '', message: '' });
  const [proposalSending, setProposalSending] = useState(false);
  const [proposalSent, setProposalSent] = useState(false);
  const [proposalError, setProposalError] = useState('');
  const [descExpanded, setDescExpanded] = useState(false);
  const [shippingExpanded, setShippingExpanded] = useState(false);
  const [selectedImage, setSelectedImage] = useState(listing?.image || null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isZooming, setIsZooming] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState('50% 50%');
  const [hasFetchedFresh, setHasFetchedFresh] = useState(false);

  React.useEffect(() => {
    if (!listing) return;
    const prevTitle = document.title;
    document.title = `${listing.title} – WatchPassion`;

    const setMeta = (name, content, prop = false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(sel);
      if (!el) {
        el = document.createElement('meta');
        prop ? el.setAttribute('property', name) : el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const desc = `${listing.title} – ${listing.condition || ''} – €${listing.price}. Acquista su WatchPassion.`;
    const canonical = `https://watchpassion.net/mercato/${listing.id}`;
    setMeta('description', desc);
    setMeta('og:title', listing.title, true);
    setMeta('og:description', desc, true);
    setMeta('og:image', listing.image || '', true);
    setMeta('og:url', canonical, true);
    setMeta('og:type', 'product', true);

    const existingLd = document.querySelector('#product-jsonld');
    if (existingLd) existingLd.remove();
    const script = document.createElement('script');
    script.id = 'product-jsonld';
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: listing.title,
      image: listing.images || [listing.image],
      description: listing.condition,
      offers: {
        '@type': 'Offer',
        price: listing.price,
        priceCurrency: listing.currency || 'EUR',
        availability: 'https://schema.org/InStock',
        url: canonical,
      },
    });
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      const s = document.querySelector('#product-jsonld');
      if (s) s.remove();
    };
  }, [listing]);

  const needsFresh = !hasFetchedFresh && (!listing || !listing.description || listing.description === 'Nessuna descrizione disponibile' || !listing.images || listing.images.length <= 1);
  const { isLoading, error } = useQuery(
    ['listing', id],
    () => fetchListingDetails(id),
    {
      enabled: needsFresh,
      onSuccess: (data) => { setListing(data); setSelectedImage(data?.image || null); setHasFetchedFresh(true); },
      refetchOnWindowFocus: false
    }
  );

  const formatPrice = (price) => new Intl.NumberFormat('it-IT', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(price);

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('it-IT', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomOrigin(`${x}% ${y}%`);
  };

  const handlePurchase = () => {
    navigate(`/checkout/${rawId}`);
  };

  const handleSendInquiry = async () => {
    if (!inquiryForm.email) return;
    setInquirySending(true);
    setInquiryError('');
    try {
      await axios.post(`${API}/inquiries`, {
        name: inquiryForm.name,
        email: inquiryForm.email,
        phone: inquiryForm.phone,
        message: inquiryForm.message,
        itemId: listing?.id,
        itemTitle: listing?.title,
      });
      setInquirySent(true);
    } catch (err) {
      setInquiryError(err?.response?.data?.error || err?.message || 'Errore di rete');
    }
    setInquirySending(false);
  };

  const handleSendProposal = async () => {
    if (!proposalForm.email || !proposalForm.proposedPrice) return;
    setProposalSending(true);
    setProposalError('');
    try {
      await axios.post(`${API}/proposals`, {
        itemId: listing?.id,
        itemTitle: listing?.title,
        itemImage: listing?.image,
        originalPrice: listing?.price,
        proposedPrice: parseFloat(proposalForm.proposedPrice),
        name: proposalForm.name,
        email: proposalForm.email,
        phone: proposalForm.phone,
        message: proposalForm.message,
      });
      setProposalSent(true);
    } catch (err) {
      setProposalError(err?.response?.data?.error || err?.message || 'Errore di rete');
    }
    setProposalSending(false);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: listing.title, url: window.location.href });
        return;
      } catch (e) {
        if (e.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(window.location.href);
      setSnackbarMessage(t('detail.linkCopied'));
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage(window.location.href);
      setSnackbarOpen(true);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Grid container spacing={{ xs: 2, md: 4 }}>
            <Grid item xs={12} md={6}><Skeleton variant="rectangular" height={600} /></Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="text" height={60} />
              <Skeleton variant="text" height={30} />
              <Skeleton variant="text" width="60%" height={40} />
              <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
              <Skeleton variant="rectangular" height={100} sx={{ mt: 2 }} />
            </Grid>
          </Grid>
        </Box>
      </Container>
    );
  }

  if (error || !listing) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">{t('detail.error')}</Alert>
          <Button variant="contained" onClick={() => navigate('/mercato')} sx={{ mt: 2 }} startIcon={<ArrowBackIcon />}>
            {t('detail.backToMarket')}
          </Button>
        </Box>
      </Container>
    );
  }

  const pageUrl = `https://watchpassion.net${window.location.pathname}`;
  const pageImage = listing.images?.[0] || listing.image || '';
  const pageDesc = [
    listing.price ? `€${parseFloat(listing.price).toFixed(2)}` : '',
    listing.condition,
    'WatchPassion — Orologi vintage e di lusso',
  ].filter(Boolean).join(' · ');

  return (
    <Container maxWidth="lg">
      <Helmet>
        <title>{listing.title} | WatchPassion</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={listing.title} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="product" />
      </Helmet>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mt: 2, mb: 1 }}>
        {t('detail.back')}
      </Button>

      <Box sx={{ py: 2 }}>
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {/* Image */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', mb: 2, position: 'relative' }}>
              {/* Zoom container */}
              <Box
                sx={{ overflow: 'hidden', cursor: 'zoom-in', position: 'relative', lineHeight: 0 }}
                onMouseMove={handleMouseMove}
                onMouseEnter={() => setIsZooming(true)}
                onMouseLeave={() => setIsZooming(false)}
                onClick={() => setLightboxOpen(true)}
              >
                {selectedImage ? (
                  <Box
                    component="img"
                    src={selectedImage}
                    alt={listing.title}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      objectFit: 'contain',
                      maxHeight: '500px',
                      margin: '0 auto',
                      p: 2,
                      transform: isZooming ? 'scale(2)' : 'scale(1)',
                      transformOrigin: zoomOrigin,
                      transition: isZooming ? 'transform 0.1s ease-out' : 'transform 0.3s ease-out',
                    }}
                  />
                ) : (
                  <Skeleton variant="rectangular" height={500} />
                )}
              </Box>

              {/* Share button */}
              <Box sx={{ position: 'absolute', top: 19, right: 19 }}>
                <IconButton
                  color="primary"
                  onClick={(e) => { e.stopPropagation(); handleShare(); }}
                  sx={{ backgroundColor: 'background.paper', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <Share />
                </IconButton>
              </Box>
            </Card>

            {/* Thumbnails */}
            {listing.images?.length > 1 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {listing.images.map((img, i) => (
                  <Box
                    key={i}
                    component="img"
                    src={img.replace(/s-l\d+/, 's-l300')}
                    alt={`${listing.title} ${i + 1}`}
                    onClick={() => setSelectedImage(img)}
                    sx={{
                      width: 72, height: 72, objectFit: 'cover', borderRadius: 1, cursor: 'pointer',
                      border: selectedImage === img ? '2px solid' : '2px solid transparent',
                      borderColor: selectedImage === img ? 'primary.main' : 'transparent',
                      opacity: selectedImage === img ? 1 : 0.65,
                      transition: 'opacity 0.2s, border-color 0.2s',
                      '&:hover': { opacity: 1 },
                    }}
                  />
                ))}
              </Box>
            )}
          </Grid>

          {/* Details */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant={{ xs: 'h5', md: 'h4' }} component="h1" gutterBottom>{listing.title}</Typography>
              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {formatPrice(listing.price)}
                </Typography>
              </Box>

              {/* Description */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {t('detail.description')}
                </Typography>
                {listing.description && listing.description !== 'Nessuna descrizione disponibile' ? (
                  <>
                    <Box
                      dangerouslySetInnerHTML={{ __html: listing.description }}
                      sx={{
                        fontSize: '0.95rem', lineHeight: 1.6,
                        maxHeight: descExpanded ? 'none' : '120px',
                        overflow: 'hidden',
                        '& font': { fontFamily: 'inherit !important' },
                      }}
                    />
                    <Button size="small" onClick={() => setDescExpanded(prev => !prev)} sx={{ mt: 0.5, p: 0, textTransform: 'none' }}>
                      {descExpanded ? t('detail.showLess') : t('detail.showMore')}
                    </Button>
                  </>
                ) : (
                  <Typography variant="body1">{t('detail.noDescription')}</Typography>
                )}
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableBody>
                    {/* Condition */}
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ border: 'none', width: '40%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocalOffer fontSize="small" color="action" sx={{ mr: 1 }} />
                          <Typography variant="body2">{t('detail.condition')}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ border: 'none' }}>
                        <Chip
                          label={(!listing.condition || listing.condition === 'N/A') ? t('detail.conditionUnknown') : listing.condition}
                          size="small" color="default"
                        />
                      </TableCell>
                    </TableRow>

                    {/* Seller */}
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ border: 'none' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Store fontSize="small" color="action" sx={{ mr: 1 }} />
                          <Typography variant="body2">{t('detail.seller')}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ border: 'none' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {listing.seller?.username || 'watch_passion_arezzo'} ({listing.seller?.feedbackPercentage || '100%'})
                          </Typography>
                          <VerifiedUser color="primary" fontSize="small" />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                          <Typography variant="caption">🇮🇹</Typography>
                          <Typography variant="caption" color="text.secondary">{t('detail.professionalSeller')}</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Published */}
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ border: 'none' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CalendarToday fontSize="small" color="action" sx={{ mr: 1 }} />
                          <Typography variant="body2">{t('detail.published')}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ border: 'none' }}>
                        <Typography variant="body2">
                          {listing.lastUpdated || listing.listingDate
                            ? formatDate(listing.lastUpdated || listing.listingDate)
                            : t('detail.dateUnavailable')}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Reference */}
                    {listing.reference && listing.reference !== 'N/A' && (
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ border: 'none' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <WatchLater fontSize="small" color="action" sx={{ mr: 1 }} />
                            <Typography variant="body2">{t('detail.reference')}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ border: 'none' }}>
                          <Typography variant="body2">{listing.reference}</Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Shipping */}
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ border: 'none' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocalShipping fontSize="small" color="action" sx={{ mr: 1 }} />
                          <Typography variant="body2">{t('detail.shipping')}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ border: 'none' }}>
                        <Box sx={{ maxHeight: shippingExpanded ? 'none' : '5em', overflow: 'hidden' }}>
                          <Typography variant="body2" dangerouslySetInnerHTML={{ __html: t('detail.shippingNational') }} />
                          <Typography variant="body2" sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: t('detail.shippingInternational') }} />
                        </Box>
                        <Button size="small" onClick={() => setShippingExpanded(p => !p)} sx={{ mt: 0.5, p: 0, textTransform: 'none' }}>
                          {shippingExpanded ? t('detail.showLess') : t('detail.showMore')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Button fullWidth variant="contained" size="large" startIcon={<ShoppingCart />} onClick={handlePurchase} sx={{ py: 1.5 }}>
                    {t('detail.buyNow')}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button fullWidth variant="outlined" size="large" startIcon={<HandshakeIcon />}
                    onClick={() => { setProposalOpen(true); setProposalSent(false); }} sx={{ py: 1.5 }}>
                    Fai un'offerta
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button fullWidth variant="outlined" size="small" startIcon={<EmailIcon />}
                    onClick={() => setInquiryOpen(true)} sx={{ color: 'text.secondary', borderColor: 'divider' }}>
                    Richiedi informazioni
                  </Button>
                </Grid>
                {listing?.url && (
                  <Grid item xs={12}>
                    <Button fullWidth variant="text" size="small" href={listing.url} target="_blank"
                      endIcon={<OpenInNewIcon />} sx={{ color: 'text.secondary' }}>
                      Vedi su eBay
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Inquiry dialog */}
      <Dialog open={inquiryOpen} onClose={() => { setInquiryOpen(false); setInquirySent(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Richiedi informazioni</DialogTitle>
        <DialogContent>
          {listing && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{listing.title}</Typography>}
          {inquiryError && <Alert severity="error" sx={{ mb: 1 }}>{inquiryError}</Alert>}
          {inquirySent ? (
            <Alert severity="success">Messaggio inviato! Ti risponderemo al più presto.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField fullWidth label="Nome" value={inquiryForm.name}
                onChange={e => setInquiryForm(f => ({ ...f, name: e.target.value }))} />
              <TextField fullWidth label="Email *" type="email" value={inquiryForm.email}
                onChange={e => setInquiryForm(f => ({ ...f, email: e.target.value }))} required />
              <TextField fullWidth label="Telefono" value={inquiryForm.phone}
                onChange={e => setInquiryForm(f => ({ ...f, phone: e.target.value }))} />
              <TextField fullWidth label="Messaggio" multiline rows={3} value={inquiryForm.message}
                onChange={e => setInquiryForm(f => ({ ...f, message: e.target.value }))} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setInquiryOpen(false); setInquirySent(false); }}>Chiudi</Button>
          {!inquirySent && (
            <Button variant="contained" onClick={handleSendInquiry}
              disabled={!inquiryForm.email || inquirySending}>
              {inquirySending ? <CircularProgress size={20} /> : 'Invia'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Proposal dialog */}
      <Dialog open={proposalOpen} onClose={() => { setProposalOpen(false); setProposalSent(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Fai un'offerta</DialogTitle>
        <DialogContent>
          {listing && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">{listing.title}</Typography>
              <Typography variant="body1" fontWeight="bold">Prezzo: €{listing.price?.toLocaleString('it-IT')}</Typography>
            </Box>
          )}
          {proposalError && <Alert severity="error" sx={{ mb: 1 }}>{proposalError}</Alert>}
          {proposalSent ? (
            <Alert severity="success">Offerta inviata! Ti contatteremo presto per confermare.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField fullWidth label="Il tuo prezzo (€) *" type="number" value={proposalForm.proposedPrice}
                onChange={e => setProposalForm(f => ({ ...f, proposedPrice: e.target.value }))}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                required />
              <TextField fullWidth label="Nome" value={proposalForm.name}
                onChange={e => setProposalForm(f => ({ ...f, name: e.target.value }))} />
              <TextField fullWidth label="Email *" type="email" value={proposalForm.email}
                onChange={e => setProposalForm(f => ({ ...f, email: e.target.value }))} required />
              <TextField fullWidth label="Telefono" value={proposalForm.phone}
                onChange={e => setProposalForm(f => ({ ...f, phone: e.target.value }))} />
              <TextField fullWidth label="Messaggio (opzionale)" multiline rows={2} value={proposalForm.message}
                onChange={e => setProposalForm(f => ({ ...f, message: e.target.value }))} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setProposalOpen(false); setProposalSent(false); }}>Chiudi</Button>
          {!proposalSent && (
            <Button variant="contained" onClick={handleSendProposal}
              disabled={!proposalForm.email || !proposalForm.proposedPrice || proposalSending}>
              {proposalSending ? <CircularProgress size={20} /> : 'Invia offerta'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Lightbox */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.92)', boxShadow: 'none' } }}
      >
        <DialogContent sx={{ p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{ position: 'absolute', top: 8, right: 8, color: 'white', backgroundColor: 'rgba(255,255,255,0.15)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' } }}
          >
            <Close />
          </IconButton>
          <Box
            component="img"
            src={selectedImage}
            alt={listing?.title}
            sx={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: 1 }}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default ListingDetailsPage;
